import qs from "query-string";
import useSWR, { useSWRInfinite } from "swr";
import flatten from "lodash/flatten";
import get from "lodash/get";
import map from "lodash/map";
import { getTabID, getToken } from "./utils/common";
import {
  apiUrl,
  fetcher,
  fetcherWithToken,
  posterWithToken,
  reserveQuota,
} from "./clientAPI";

const useFetchedHomeInfo = () => {
  const { data, error, mutate } = useSWR(`/api/v1/videos/`, fetcher, {
    revalidateOnFocus: false,
  });

  const { menus = [], banners = [], slots = [], recommends = {} } = data || {};
  const isPending = !data;
  const setHomeInfo = mutate;
  return { menus, banners, slots, recommends, setHomeInfo, error, isPending };
};

const useFetchedTopicVideos = ({
  topicId,
  sortKey = "played",
  tagName = "",
  skip = 0,
  limit = 12,
}) => {
  const tagNameSearch = `&tag_name=${tagName}`;
  const url = `/api/v1/videos/topic/${topicId ||
    "all"}?skip=${skip}&limit=${limit}&sort_key=${sortKey}${tagNameSearch}`;
  const { data, error, mutate } = useSWR(url, fetcher);

  const topic = data || {};
  const isPending = !data;
  const setTopicVideos = mutate;
  return { topic, isPending, setTopicVideos };
};

const useFetchedTopicVideosInfinite = ({
  topicId = "all",
  sortKey = "played",
  tagNames = [],
  page_size = 24,
}) => {
  const tagNameSearch = `&${qs.stringify({ tag_name: tagNames })}`;
  const { data, error, mutate, size, setSize, isValidating } = useSWRInfinite(
    index =>
      `/api/v1/videos/topic/${topicId ||
        "all"}?sort_key=${sortKey}${tagNameSearch}&page=${index +
        1}&per_page=${page_size}`,
    fetcher,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false
    }
  );

  const items = flatten(map(data, "items"));
  const topic = data
    ? {
        topic_id: get(data, "0.topic_id"),
        topic_name: get(data, "0.topic_name", ""),
        tag_name: get(data, "0.tag_name", ""),
        tags: get(data, "0.tags", []),
        count: get(data, "0.count"),
        items,
      }
    : {};
  const isLoadingInitialData = !data && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === "undefined");
  const isEmpty = data?.[0]?.items?.length === 0;
  const isNotEnough = data?.[data.length - 1]?.count <= page_size;
  const isReachingEnd =
    isEmpty ||
    isNotEnough ||
    (data && data[data.length - 1]?.items?.length < page_size);
  const isRefreshing = isValidating && data && data.length === size;
  const setTopicVideos = mutate;
  return {
    topic,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    size,
    setSize,
    setTopicVideos,
  };
};

const useFetchedVideoInfo = videoId => {
  const { data, error, mutate } = useSWR(
    `/api/v1/videos/detail/${videoId}`,
    fetcher,
  );

  const videoInfo = data || {};
  const isPending = !data;
  const setVideoInfo = mutate;
  return { videoInfo, setVideoInfo, error, isPending };
};

const useFetchedWatchHistory = () => {
  const { data, error, mutate } = useSWR(
    `/api/v1/users/watched`,
    fetcherWithToken,
  );

  const histories = data || [];
  const isPending = !data;
  const setWatchHistory = mutate;
  return { histories, setWatchHistory, error, isPending };
};

const useFetchedCollections = () => {
  const { data, error, mutate } = useSWR(
    `/api/v1/users/collections`,
    fetcherWithToken,
  );

  const collections = data || [];
  const isPending = !data;
  const setCollections = mutate;
  return { collections, setCollections, error, isPending };
};

const useFetchedCollectionVideos = collection_id => {
  const { data, error, mutate } = useSWR(
    `/api/v1/users/collection/${collection_id}`,
    fetcherWithToken,
  );

  const collectionVideos = data || {};
  const isPending = !data;
  const setCollectionVideos = mutate;
  return { collectionVideos, setCollectionVideos, error, isPending };
};

const useFetchedVideoCollections = (videoId, isAuthenticated) => {
  const { data, error, mutate } = useSWR(
    videoId && isAuthenticated
      ? `/api/v1/users/video/${videoId}/collections`
      : null,
    fetcherWithToken,
  );

  const videoCollections = data || { video_id: videoId, collections: [] };
  const isPending = !data;
  const setVideoCollections = mutate;
  return { videoCollections, setVideoCollections, error, isPending };
};

const useFetchedHeartbeat = videoId => {
  const tabId = getTabID();
  const { data, error, mutate } = useSWR(
    `/api/v1/videos/heartbeat/${videoId}?tid=${tabId}`,
    posterWithToken,
  );

  const heartbeat = data || {};

  const isPending = !data;
  const setHeartbeat = mutate;
  return { heartbeat, setHeartbeat, error, isPending };
};

const useFetchedQuota = (refreshInterval = 0) => {
  const tabId = getTabID();
  const { data, error, mutate } = useSWR(
    getToken() ? `/api/v1/videos/quota?tid=${tabId}` : null,
    fetcherWithToken,
    {
      refreshInterval,
    },
  );

  const quota = data || {};

  const isPending = !data;
  const setQuota = mutate;
  return { quota, setQuota, error, isPending };
};

const useFetchedSearch = ({ query, sortKey = "played", tagName = "" }) => {
  const tagNameSearch = `&tag_name=${tagName}`;
  const { data, error, mutate } = useSWR(
    `/api/v1/videos/search?q=${query}&sort_key=${sortKey}${tagNameSearch}`,
    fetcherWithToken,
  );

  const searchedVideos = data || {};
  const { items = undefined, tags = undefined } = searchedVideos;
  const isPending = !data;
  const setSearchedVideos = mutate;
  return { searchedVideos, items, tags, setSearchedVideos, error, isPending };
};

const useFetchedSearchInfinite = ({
  query,
  sortKey = "relevant",
  tagNames = [],
  page_size = 24,
}) => {
  const tagNameSearch = `&${qs.stringify({ tag_name: tagNames })}`;
  const { data, error, mutate, size, setSize, isValidating } = useSWRInfinite(
    index =>
      `/api/v1/videos/search?q=${query}&sort_key=${sortKey}${tagNameSearch}&page=${index +
        1}&per_page=${page_size}`,
    fetcherWithToken,
  );

  const searchedVideos = data
    ? {
        tags: get(data, "0.tags"),
        items: flatten(map(data, "items")),
      }
    : {};
  const { items = undefined, tags = undefined } = searchedVideos;

  // const isPending = !data;
  const isLoadingInitialData = !data && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === "undefined");
  const isEmpty = data?.[0]?.items?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.items?.length < page_size);
  const isRefreshing = isValidating && data && data.length === size;
  const setSearchedVideos = mutate;
  return {
    searchedVideos,
    items,
    tags,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    size,
    setSize,
    setSearchedVideos,
  };
};

const useHomeInfo = () => {
  const {
    menus,
    banners,
    slots,
    recommends,
    isPending,
    setHomeInfo,
  } = useFetchedHomeInfo();

  return { menus, banners, slots, recommends, isPending };
};

const useTopicVideos = ({ topicId, sortKey, tagName, skip, limit }) => {
  const { topic, isPending, setTopicVideos } = useFetchedTopicVideos({
    topicId,
    sortKey,
    tagName,
    skip,
    limit,
  });

  return { topic, isPending, setTopicVideos };
};

const useTopicVideosInfinite = ({ topicId, sortKey, tagNames, page_size }) => {
  const {
    topic,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    size,
    setSize,
    setTopicVideos,
  } = useFetchedTopicVideosInfinite({
    topicId,
    sortKey,
    tagNames,
    page_size,
  });
  return {
    topic,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    size,
    setSize,
    setTopicVideos,
  };
};

const useVideoInfo = videoId => {
  const { videoInfo, isPending, setVideoInfo } = useFetchedVideoInfo(videoId);

  return { videoInfo, isPending, setVideoInfo };
};

const useWatchHistory = videoId => {
  const { histories, isPending, setWatchHistory } = useFetchedWatchHistory();

  return { histories, isPending, setWatchHistory };
};

const useCollections = () => {
  const { collections, isPending, setCollections } = useFetchedCollections();

  return { collections, isPending, setCollections };
};

const useCollectionVideos = collection_id => {
  const {
    collectionVideos,
    isPending,
    setCollectionVideos,
  } = useFetchedCollectionVideos(collection_id);

  return { collectionVideos, isPending, setCollectionVideos };
};

const useVideoCollections = (videoId, isAuthenticated) => {
  const {
    videoCollections,
    isPending,
    setVideoCollections,
  } = useFetchedVideoCollections(videoId, isAuthenticated);

  return { videoCollections, isPending, setVideoCollections };
};

const useQuota = ({ videoId, refreshInterval }) => {
  const { quota, isPending, setQuota } = useFetchedQuota(refreshInterval);
  const isQuotaEnough =
    quota.device_in_use + 1 <= quota.device_limit
      ? quota.device_in_use + 1
      : quota.device_limit;
  const reserveQuotaFromStart = (dispatch, history) => {
    return reserveQuota(videoId).catch(err => {
      console.log("reserveQuota wrong:", err);
      dispatch({ type: "SET_UNKNOWN_ERROR" });
    });
  };
  const checkQuotaFromPauseToPlay = () => {
    return reserveQuota(videoId);
  };
  return {
    quota,
    isQuotaEnough,
    isPending,
    setQuota,
    reserveQuotaFromStart,
    checkQuotaFromPauseToPlay,
  };
};

const useHeartbeat = videoId => {
  const { heartbeat, isPending, setHeartbeat } = useFetchedHeartbeat(videoId);

  return { heartbeat, isPending, setHeartbeat };
};

const useSearchVideos = ({ query, sortKey, tagName }) => {
  const {
    searchedVideos,
    items,
    tags,
    setSearchedVideos,
    isPending,
  } = useFetchedSearch({ query, sortKey, tagName });

  return { searchedVideos, items, tags, isPending, setSearchedVideos };
};

const useSearchVideosInfinite = ({ query, sortKey, tagNames }) => {
  const {
    searchedVideos,
    items,
    tags,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    size,
    setSize,
    setSearchedVideos,
  } = useFetchedSearchInfinite({ query, sortKey, tagNames });

  return {
    searchedVideos,
    items,
    tags,
    isLoadingInitialData,
    isLoadingMore,
    isEmpty,
    isReachingEnd,
    isRefreshing,
    size,
    setSize,
    setSearchedVideos,
  };
};

export {
  useHomeInfo,
  useTopicVideos,
  useVideoInfo,
  useWatchHistory,
  useCollections,
  useCollectionVideos,
  useVideoCollections,
  useQuota,
  useHeartbeat,
  useSearchVideos,
  useTopicVideosInfinite,
  useSearchVideosInfinite,
};
